/**
 * Règle unique de format d'adresse e-mail.
 * Partagée entre le serveur (server/utils/validation.js) et le navigateur
 * (src/utils/validation.js, attribut `pattern` des formulaires) : une seule
 * source de vérité, comme shared/password-rules.js pour les mots de passe.
 *
 * Ce que la règle exige, et que `type="email"` natif n'exige PAS :
 * un domaine COMPLET, c'est-à-dire au moins un point suivi d'une extension
 * d'au moins deux lettres. « alertis@gmail » est rejeté, « alertis@gmail.com »
 * est accepté.
 *
 * Volontairement plus simple que la RFC 5322 : elle doit rester lisible,
 * tenir dans un attribut HTML `pattern`, et ne jamais refuser une adresse
 * réellement utilisée par un client.
 */

/** Longueur maximale d'une adresse (RFC 5321). */
export const EMAIL_MAX_LENGTH = 254;

/**
 * Motif sans ancres, tel qu'il peut être posé dans un attribut HTML `pattern`
 * (l'attribut ancre lui-même le motif sur toute la valeur).
 */
export const EMAIL_PATTERN_SOURCE = '[^\\s@]+@[^\\s@.]+(\\.[^\\s@.]+)*\\.[A-Za-z]{2,}';

/** Même motif, ancré, pour une validation en JavaScript. */
export const EMAIL_REGEX = new RegExp(`^${EMAIL_PATTERN_SOURCE}$`);

/** Message affiché au visiteur comme au commercial quand le format est refusé. */
export const EMAIL_INVALID_MESSAGE = "L'adresse e-mail n'est pas valide";

/** Ce qu'il faut corriger, en une phrase, sous le message ci-dessus. */
export const EMAIL_INVALID_DESCRIPTION =
  "Vérifiez-la : le nom de domaine doit être complet, comme nom@exemple.fr.";

/**
 * @param {unknown} email
 * @returns {boolean} vrai si l'adresse est d'un format acceptable
 */
export function isValidEmail(email) {
  if (typeof email !== 'string') return false;
  const value = email.trim();
  if (!value || value.length > EMAIL_MAX_LENGTH) return false;
  return EMAIL_REGEX.test(value);
}

/**
 * CE QUI NE VA PAS DANS UNE ADRESSE, en une phrase : « adresse invalide » seul
 * laissait la personne deviner. L'accent est le cas le plus fréquent — les
 * navigateurs refusent « frédéric@… » avant même l'envoi, et notre règle
 * aussi : les messageries ne les acheminent pas de façon fiable.
 *
 * @param {unknown} email
 * @returns {string|null} la phrase à afficher, ou `null` si l'adresse convient
 */
export function raisonEmailInvalide(email) {
  const value = typeof email === 'string' ? email.trim() : '';
  if (!value) return null;
  if (/[^\x00-\x7F]/.test(value)) {
    return 'Une adresse e-mail ne peut pas contenir d’accent ni de caractère spécial (é, è, ç, ô…) : écrivez-la sans.';
  }
  if (/\s/.test(value)) return 'Une adresse e-mail ne contient pas d’espace.';
  if (value.includes(',')) return 'Une virgule s’est glissée dans l’adresse e-mail : remplacez-la par un point.';
  if (value.split('@').length !== 2) return 'Une adresse e-mail contient un seul @, comme nom@exemple.fr.';
  if (value.length > EMAIL_MAX_LENGTH) return `Une adresse e-mail ne dépasse pas ${EMAIL_MAX_LENGTH} caractères.`;
  if (!EMAIL_REGEX.test(value)) return `${EMAIL_INVALID_MESSAGE}. ${EMAIL_INVALID_DESCRIPTION}`;
  return null;
}
